import type { Dayjs } from "dayjs";
import { CategoryGroupType, type IOfferTile, type IOfferTileDto } from ".";
import dayjs from "dayjs";

const DAYS_OFFER_CONSIDERED_LATEST = 30;
const DAYS_OFFER_CONSIDERED_ENDING_SOON = 30;

export class OfferTile implements IOfferTile
{
    
    offerId: string;
    title: string;
    categoryId: string;
    categoryName: string;
    categoryGroupType: CategoryGroupType;
    offerDistance?: number; 
    advertiserId: string;
    advertiserName: string;
    advertiserLogo: string;
    coverImages: string[];
    reward?: string;
    offerType: string;
    isInstoreOffer: boolean;
    linkUrl?: string; 
    isCardLinked: boolean;
    isAutomotive: boolean;
    isLifestyle: boolean;
    isGiftCard: boolean;
    utcStartDate?: Dayjs    
    utcEndDate?: Dayjs      
    utcFeatureFromDate?: Dayjs    
    utcFeatureToDate?: Dayjs      
    featureCoverImageUrl?: string          

    constructor( model: IOfferTileDto )
    {
        this.offerId = model.offerId;
        this.title = model.title;
        this.categoryId = model.categoryId;
        this.categoryName = model.categoryName;
        this.categoryGroupType = model.categoryGroupType;
        this.offerDistance = model.offerDistance;
        this.advertiserId = model.advertiserId;
        this.advertiserName = model.advertiserName;
        this.advertiserLogo = model.advertiserLogo;
        this.coverImages = model.coverImages;
        this.reward = this.getReward( model.title ) ?? this.getCentsPerLitreReward( model.advertiserName ); 
        this.offerType = model.offerType;
        this.isInstoreOffer = ( model.offerType ?? '' ).toLowerCase() == 'instore';
        this.linkUrl = model.linkUrl;
        this.isCardLinked = model.isCardLinked;
        this.isAutomotive = this.categoryGroupType == CategoryGroupType.Automotive ? true : false;
        this.isLifestyle = this.isAutomotive == false;
        this.isGiftCard = model.offerType.toLowerCase() === "giftcard";

        this.utcStartDate = model.utcStartDate ? dayjs( model.utcStartDate ) : undefined;
        this.utcEndDate = model.utcEndDate ? dayjs( model.utcEndDate ) : undefined;
        this.utcFeatureFromDate = model.utcFeatureFromDate ? dayjs( model.utcFeatureFromDate ) : undefined;
        this.utcFeatureToDate = model.utcFeatureToDate ? dayjs( model.utcFeatureToDate ) : undefined;
        this.featureCoverImageUrl = model.featureCoverImageUrl;
    }
    
    /**
     * True if it is an online offer
     */
    get isOnlineOffer()
    {
        return this.isInstoreOffer == false;
    }

    /**
     * True if the offer has a link url
     */
    get hasLink()
    {
        return ( this.linkUrl ?? '' ).length > 0;
    }

    /**
     * True if the offer is in-store and has a link URL that leads to booking
     */
    get hasBookingLink()
    {
        return this.isInstoreOffer && ( this.linkUrl ?? '' ).trim().length > 0;
    }
    
    /**
     * True if the offer is ending soon
     */
    get isEndingSoon()
    {
        return ( this.utcEndDate && 
               this.utcEndDate.isValid() && 
               this.utcEndDate.isBefore( dayjs().add( DAYS_OFFER_CONSIDERED_ENDING_SOON, 'days' ) ) ) ?? false;
    }

    /**
     * True if the offer has been recently added
     */
    get isRecentlyAdded()
    {
        return ( this.utcStartDate && this.utcStartDate.isValid() 
              && this.utcStartDate.isAfter( dayjs().subtract( DAYS_OFFER_CONSIDERED_LATEST, 'days' ) ) ) ?? false;
    }

    /**
     * True if the offer is featured
     */
    get isFeatured()
    {
        const today = dayjs().date();
        return ( this.utcFeatureFromDate?.isValid() && this.utcFeatureFromDate.date() <= today
              && this.utcFeatureToDate?.isValid() && this.utcFeatureToDate.date() > today ) 
            || false;
    }
    
    /**
     * True if the offer is a special feature
     */
    get isSpecialFeature()
    {
        return this.featureCoverImageUrl != undefined && this.isFeatured;
    }
    
    /**
     * A description of the offer type / card link state
     */
    get offerTypeDescription(): string | undefined
    {
        
        if( this.isCardLinked && this.isOnlineOffer )
        {
            return 'Online Offer'; // The offer can be taken up anywhere online
        }

        else if( this.isCardLinked && this.isInstoreOffer )
        {
            return 'In Store Offer'; // The offer can only be taken up instore
        }

        else if( this.isCardLinked == false && this.isOnlineOffer )
        {
            return 'In App Offer'; // The offer can only be taken up in the app
        }

        else
        {
            return undefined;
        }

    }

    /**
     * Gets the reward from the offer title
     */
    getReward( title: string )
    {
        const regex = /([$]*[\d*.*\d]+[%*$*]* cashback)/gmi;
        const match = regex.exec( title );
        if( match == null ) return undefined
        return match[ 0 ];
    }

    /**
     * Gets the cents per litre reward from the advertiser
     */
    getCentsPerLitreReward( parseString: string )
    {
        const regex = /([$]*[\d*.*\d]+c* per litre)/gmi;
        const match = regex.exec( parseString );
        if( match == null ) return undefined
        return match[ 0 ];
    }
    
}